import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiSun, FiMoon } from 'react-icons/fi';
import { CiDollar, CiStar, CiWallet } from "react-icons/ci";
import { FaDollarSign } from 'react-icons/fa';

const Header = ({ theme, toggleTheme }) => {
    const [balance, setBalance] = useState(
        Number(localStorage.getItem("balance") || 0)
    );
    const [UsdtBalance, setUsdtBalance] = useState(
        Number(localStorage.getItem("usdt_balance") || 0)
    );

    // Load theme from localStorage on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme && toggleTheme && savedTheme !== theme) {
            if (
                (savedTheme === 'dark' && theme === 'light') ||
                (savedTheme === 'light' && theme === 'dark')
            ) {
                toggleTheme();
            }
        }
    }, []);

    // Persist theme on change
    useEffect(() => {
        if (theme) {
            localStorage.setItem("theme", theme);
        }
    }, [theme]);

    // Listen for balance updates
    useEffect(() => {
        const handleBalanceChange = () => {
            const newBalance = parseFloat(localStorage.getItem("balance") || 0);
            const newUsdtBalance = parseFloat(localStorage.getItem("usdt_balance") || 0);
            setBalance(newBalance);
            setUsdtBalance(newUsdtBalance);
        };

        window.addEventListener("storage", handleBalanceChange);
        window.addEventListener("balanceUpdated", handleBalanceChange);

        return () => {
            window.removeEventListener("storage", handleBalanceChange);
            window.removeEventListener("balanceUpdated", handleBalanceChange);
        };
    }, []);

    const headerStyles = {
        light: {
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            textColor: 'text-dark',
            balanceBg: 'bg-light',
            balanceText: 'text-dark',
            buttonBg: '#f8f9fa',
            borderClass: 'border-bottom border-light'
        },
        dark: {
            backgroundColor: 'rgba(0, 0, 0, 0.98)',
            textColor: 'text-light',
            balanceBg: 'bg-dark',
            balanceText: 'text-light',
            buttonBg: '#343a40',
            borderClass: 'border-bottom border-secondary'
        }
    };

    const currentTheme = headerStyles[theme];

    const formatAmount = (value) =>
        parseFloat(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    return (
        <motion.div
            className={`position-fixed top-0 start-50 translate-middle-x z-3 ${currentTheme.borderClass}`}
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{
                width: '400px',
                maxWidth: '400px',
                height: '70px',
                backdropFilter: 'blur(10px)',
                backgroundColor: currentTheme.backgroundColor
            }}
        >
            <div className="d-flex justify-content-between align-items-center h-100 px-3">
                {/* App Name */}
                <motion.div
                    className="d-flex align-items-center"
                    whileHover={{ scale: 1.05 }}
                >
                    <h1 className={`h4 fw-bold mb-0 ${theme === 'light' ? 'text-primary' : 'text-info'}`}>
                        TradeBid
                    </h1>
                </motion.div>

                {/* Balance + Theme Toggle */}
                <div className="d-flex align-items-center gap-3">
                    {/* Row-wise balances: first Balance, then USDT */}
                    <motion.div
                        className={`d-flex align-items-center rounded-pill px-3 py-1 ${currentTheme.balanceBg} ${currentTheme.balanceText}`}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                        style={{
                            fontSize: '0.8rem',
                            gap: '0.75rem',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {/* Balance row */}
                        <div className="d-flex align-items-center gap-0">
                            <CiWallet size={16} />
                            <span className="fw-bold">
                                {formatAmount(balance)}
                            </span>
                        </div>

                        {/* Divider */}
                        <span
                            style={{
                                width: '1px',
                                height: '18px',
                                backgroundColor: theme === 'light' ? '#dee2e6' : '#4b5563'
                            }}
                        />

                        {/* USDT row */}
                        <div className="d-flex align-items-center gap-0">
                            <FaDollarSign size={12} />
                            <span className="fw-bold">
                                {formatAmount(UsdtBalance)}
                            </span>
                        </div>
                    </motion.div>

                    {/* Theme Toggle Button */}
                    <motion.button
                        className={`btn border-0 d-flex align-items-center justify-content-center rounded-circle ${theme === 'light' ? 'btn-outline-secondary' : 'btn-outline-light'}`}
                        onClick={toggleTheme}
                        whileHover={{ scale: 1.1, rotate: 180 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: currentTheme.buttonBg
                        }}
                    >
                        {theme === 'light' ? (
                            <FiMoon size={18} className={theme === 'light' ? 'text-dark' : 'text-light'} />
                        ) : (
                            <FiSun size={18} className="text-warning" />
                        )}
                    </motion.button>
                </div>
            </div>
        </motion.div>
    );
};

export default Header;
